import { tokenService } from '@features/authentication/services/tokenService';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import type { GateStateSnapshot } from '../types/systemHealth.types';

/**
 * EventSource wrapper for the live concurrency-gate SSE stream (86d3peur0 Phase C) — mirrors the
 * quotation/sales-order realtime streams. Each message is a FULL `GateStateSnapshot` (not an incremental
 * event), pushed ~1/s. EventSource can't set headers, so the JWT rides as `?access_token=`; the API's
 * `JwtBearerEvents.OnMessageReceived` accepts it only on this whitelisted path. Auto-reconnects with
 * exponential backoff. Lifecycle (open/close on visibility + unmount) is owned by `useGateStateStream`.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
const STREAM_PATH = '/admin/health/gate/stream';
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

export type GateConnectionState = 'connecting' | 'open' | 'reconnecting';

export interface GateStreamHandlers {
  onSnapshot: (snapshot: GateStateSnapshot) => void;
  onConnectionStateChange?: (state: GateConnectionState) => void;
}

export interface GateStreamHandle {
  close: () => void;
}

export function openGateStateStream(handlers: GateStreamHandlers): GateStreamHandle {
  let source: EventSource | null = null;
  let backoff = MIN_BACKOFF_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  let hasConnectedOnce = false;

  const emitState = (next: GateConnectionState) => handlers.onConnectionStateChange?.(next);

  function buildUrl(): string {
    const accessToken = tokenService.getAccessToken();
    const params = new URLSearchParams();
    if (accessToken) params.set('access_token', accessToken);
    return `${API_BASE_URL}${STREAM_PATH}?${params.toString()}`;
  }

  function connect() {
    if (closed) return;
    emitState(hasConnectedOnce ? 'reconnecting' : 'connecting');
    try {
      source = new EventSource(buildUrl());
    } catch {
      scheduleReconnect();
      return;
    }

    source.onopen = () => {
      backoff = MIN_BACKOFF_MS;
      hasConnectedOnce = true;
      emitState('open');
    };
    source.onmessage = (msg) => {
      if (!msg.data) return;
      try {
        handlers.onSnapshot(JSON.parse(msg.data) as GateStateSnapshot);
      } catch (err) {
        console.warn('Failed to parse gate-state snapshot:', err, msg.data);
      }
    };
    source.onerror = () => {
      source?.close();
      source = null;
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (closed) return;
    emitState('reconnecting');
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      // E7 item 4 — refresh before reconnecting; see quotationEventStream for the full rationale.
      await ensureFreshAccessTokenForStream();
      if (closed) return;
      connect();
    }, backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  }

  connect();

  return {
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
    },
  };
}
